import type { NativeCheck, NativesSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt } from '../util/safe.js';

/**
 * Детект подмены нативных функций.
 *
 * Спуфер обязан что-то переопределить: геттер `navigator.hardwareConcurrency`,
 * `WebGLRenderingContext.prototype.getParameter`, `toDataURL`. Сделать это
 * незаметно тяжелее, чем кажется, потому что нативные функции отличаются от
 * обычных JS-функций сразу по нескольким независимым признакам:
 *
 *  1. `toString()` возвращает `{ [native code] }`;
 *  2. у нативного метода нет собственного свойства `prototype`;
 *  3. нативный геттер бросает TypeError, если позвать его с чужим `this`;
 *  4. свойство лежит на прототипе, а не на самом экземпляре;
 *  5. в свежем realm (iframe) исходник тот же самый.
 *
 * Заглушить первый признак умеют почти все — переопределяют `toString`.
 * Заглушить все пять одновременно не умеет почти никто.
 */

const NATIVE_CODE = /^\s*function[^(]*\([^)]*\)\s*\{\s*\[native code\]\s*\}\s*$/;

interface Target {
  path: string;
  /** Ожидаемый вид свойства — расхождение само по себе улика. */
  kind: 'method' | 'getter';
}

const TARGETS: Target[] = [
  { path: 'Function.prototype.toString', kind: 'method' },
  { path: 'Function.prototype.bind', kind: 'method' },
  { path: 'Object.getOwnPropertyDescriptor', kind: 'method' },
  { path: 'Object.defineProperty', kind: 'method' },
  { path: 'Reflect.get', kind: 'method' },

  { path: 'Navigator.prototype.userAgent', kind: 'getter' },
  { path: 'Navigator.prototype.platform', kind: 'getter' },
  { path: 'Navigator.prototype.hardwareConcurrency', kind: 'getter' },
  { path: 'Navigator.prototype.deviceMemory', kind: 'getter' },
  { path: 'Navigator.prototype.languages', kind: 'getter' },
  { path: 'Navigator.prototype.language', kind: 'getter' },
  { path: 'Navigator.prototype.plugins', kind: 'getter' },
  { path: 'Navigator.prototype.webdriver', kind: 'getter' },
  { path: 'Navigator.prototype.maxTouchPoints', kind: 'getter' },
  { path: 'Navigator.prototype.vendor', kind: 'getter' },
  { path: 'Navigator.prototype.mediaDevices', kind: 'getter' },
  { path: 'Navigator.prototype.geolocation', kind: 'getter' },
  { path: 'Navigator.prototype.gpu', kind: 'getter' },
  { path: 'NavigatorUAData.prototype.getHighEntropyValues', kind: 'method' },

  { path: 'Screen.prototype.width', kind: 'getter' },
  { path: 'Screen.prototype.height', kind: 'getter' },
  { path: 'Screen.prototype.availWidth', kind: 'getter' },
  { path: 'Screen.prototype.availHeight', kind: 'getter' },
  { path: 'Screen.prototype.colorDepth', kind: 'getter' },

  { path: 'HTMLCanvasElement.prototype.toDataURL', kind: 'method' },
  { path: 'HTMLCanvasElement.prototype.toBlob', kind: 'method' },
  { path: 'HTMLCanvasElement.prototype.getContext', kind: 'method' },
  { path: 'CanvasRenderingContext2D.prototype.getImageData', kind: 'method' },
  { path: 'CanvasRenderingContext2D.prototype.measureText', kind: 'method' },
  { path: 'CanvasRenderingContext2D.prototype.fillText', kind: 'method' },
  { path: 'OffscreenCanvas.prototype.getContext', kind: 'method' },

  { path: 'WebGLRenderingContext.prototype.getParameter', kind: 'method' },
  { path: 'WebGLRenderingContext.prototype.getExtension', kind: 'method' },
  { path: 'WebGLRenderingContext.prototype.getSupportedExtensions', kind: 'method' },
  { path: 'WebGLRenderingContext.prototype.readPixels', kind: 'method' },
  { path: 'WebGL2RenderingContext.prototype.getParameter', kind: 'method' },

  { path: 'AudioBuffer.prototype.getChannelData', kind: 'method' },
  { path: 'AudioBuffer.prototype.copyFromChannel', kind: 'method' },
  { path: 'OfflineAudioContext.prototype.startRendering', kind: 'method' },
  { path: 'AnalyserNode.prototype.getFloatFrequencyData', kind: 'method' },

  { path: 'Performance.prototype.now', kind: 'method' },
  { path: 'Date.prototype.getTimezoneOffset', kind: 'method' },
  { path: 'Intl.DateTimeFormat.prototype.resolvedOptions', kind: 'method' },

  { path: 'Element.prototype.getBoundingClientRect', kind: 'method' },
  { path: 'Element.prototype.getClientRects', kind: 'method' },
  { path: 'Range.prototype.getBoundingClientRect', kind: 'method' },
  { path: 'Document.prototype.createElement', kind: 'method' },

  { path: 'MediaDevices.prototype.enumerateDevices', kind: 'method' },
  { path: 'MediaDevices.prototype.getUserMedia', kind: 'method' },
  { path: 'MediaStreamTrack.prototype.getSettings', kind: 'method' },
  { path: 'MediaStreamTrack.prototype.getCapabilities', kind: 'method' },
  { path: 'HTMLMediaElement.prototype.canPlayType', kind: 'method' },
  { path: 'HTMLVideoElement.prototype.requestVideoFrameCallback', kind: 'method' },
  { path: 'MediaSource.isTypeSupported', kind: 'method' },
  { path: 'SpeechSynthesis.prototype.getVoices', kind: 'method' },
  { path: 'Permissions.prototype.query', kind: 'method' },
  { path: 'Storage.prototype.getItem', kind: 'method' },
  { path: 'RTCPeerConnection.prototype.createOffer', kind: 'method' },
  { path: 'RTCPeerConnection.prototype.setLocalDescription', kind: 'method' },
  { path: 'Geolocation.prototype.getCurrentPosition', kind: 'method' },
  { path: 'GPU.prototype.requestAdapter', kind: 'method' },
];

/** Экземпляры, у которых проверяем, не затенено ли прототипное свойство. */
const INSTANCE_OF_PROTOTYPE: Record<string, () => object | null> = {
  'Navigator.prototype': () => navigator,
  'Screen.prototype': () => screen,
};

interface Resolved {
  holder: object;
  key: string;
  fn: unknown;
  isGetter: boolean;
}

function resolvePath(root: unknown, path: string): Resolved | null {
  return attempt(() => {
    const parts = path.split('.');
    const key = parts.pop();
    if (!key) return null;

    let holder: unknown = root;
    for (const part of parts) {
      if (holder === null || holder === undefined) return null;
      holder = (holder as Record<string, unknown>)[part];
    }
    if (holder === null || typeof holder !== 'object') {
      if (typeof holder !== 'function') return null;
    }

    const descriptor = Object.getOwnPropertyDescriptor(holder as object, key);
    if (!descriptor) return null;

    return {
      holder: holder as object,
      key,
      fn: descriptor.get ?? descriptor.value,
      isGetter: typeof descriptor.get === 'function',
    };
  });
}

function sourceOf(fn: unknown, toString: (fn: unknown) => string): string | null {
  return attempt(() => toString(fn));
}

/** Свежий same-origin iframe как эталонный realm. */
function createPristineRealm(): { win: Window; dispose: () => void } | null {
  return attempt(() => {
    if (typeof document === 'undefined') return null;
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;border:0;';
    frame.src = 'about:blank';
    document.body.appendChild(frame);
    const win = frame.contentWindow;
    if (!win) {
      frame.remove();
      return null;
    }
    return { win, dispose: () => attempt(() => frame.remove()) };
  });
}

export const nativesModule = defineModule({
  id: 'natives',
  phase: 1,
  timeoutMs: 6000,
  run(): NativesSignals {
    // Собственную ссылку на toString берём один раз и до всех проверок:
    // если её подменили, это выяснится отдельной проверкой ниже.
    const localToString = Function.prototype.toString;
    const toStr = (fn: unknown): string => localToString.call(fn as () => void);

    const realm = createPristineRealm();
    const pristineToString = realm
      ? attempt(() => (realm.win as unknown as Record<string, any>)['Function'].prototype.toString)
      : null;

    const checks: NativeCheck[] = [];

    try {
      for (const target of TARGETS) {
        const resolved = resolvePath(globalThis, target.path);

        if (!resolved || resolved.fn === undefined || resolved.fn === null) {
          checks.push({
            path: target.path,
            missing: true,
            toStringTampered: false,
            descriptorTampered: false,
            ownPropertyShadow: false,
            foreignReceiverAccepted: false,
            unexpectedPrototype: false,
            realmMismatch: false,
            toStringSample: null,
          });
          continue;
        }

        const { holder, key, fn, isGetter } = resolved;
        const source = sourceOf(fn, toStr);

        // 1. Исходник должен выглядеть нативным.
        const toStringTampered = source !== null && !NATIVE_CODE.test(source);

        // 2. Нативные методы и геттеры не являются конструкторами,
        //    поэтому собственного `prototype` у них нет.
        const unexpectedPrototype =
          attempt(
            () => typeof fn === 'function' && Object.getOwnPropertyNames(fn).includes('prototype'),
            false,
          ) ?? false;

        // 3. Вид свойства должен совпадать с ожидаемым.
        const descriptorTampered = isGetter !== (target.kind === 'getter');

        // 4. Прототипное свойство не должно дублироваться на экземпляре.
        const prototypePath = target.path.slice(0, target.path.lastIndexOf('.'));
        const instanceFactory = INSTANCE_OF_PROTOTYPE[prototypePath];
        const ownPropertyShadow = instanceFactory
          ? (attempt(() => {
              const instance = instanceFactory();
              return instance
                ? Object.getOwnPropertyDescriptor(instance, key) !== undefined
                : false;
            }, false) ?? false)
          : false;

        // 5. Нативный геттер обязан отвергнуть чужой receiver.
        const foreignReceiverAccepted =
          isGetter && typeof fn === 'function'
            ? (attempt(() => {
                (fn as (this: unknown) => unknown).call({});
                return true; // не бросил — значит, это обычная JS-функция
              }, false) ?? false)
            : false;

        // 6. Тот же метод в чистом realm должен иметь тот же исходник.
        let realmMismatch = false;
        if (realm && pristineToString) {
          const pristine = resolvePath(realm.win, target.path);
          if (pristine && pristine.fn) {
            const pristineSource = attempt(() =>
              (pristineToString as (this: unknown) => string).call(pristine.fn),
            );
            realmMismatch =
              pristineSource !== null && source !== null && pristineSource !== source;
          }
        }

        void holder;

        checks.push({
          path: target.path,
          missing: false,
          toStringTampered,
          descriptorTampered,
          ownPropertyShadow,
          foreignReceiverAccepted,
          unexpectedPrototype,
          realmMismatch,
          toStringSample: source && source.length <= 200 ? source : (source?.slice(0, 200) ?? null),
        });
      }
    } finally {
      realm?.dispose();
    }

    const tampered = checks.filter(
      (c) =>
        !c.missing &&
        (c.toStringTampered ||
          c.descriptorTampered ||
          c.ownPropertyShadow ||
          c.foreignReceiverAccepted ||
          c.unexpectedPrototype ||
          c.realmMismatch),
    );

    // Подмена самого toString — верхний уровень маскировки: она делает
    // проверку №1 бесполезной для всех остальных целей.
    const toStringItselfTampered =
      (attempt(() => {
        const own = Object.getOwnPropertyNames(localToString);
        return !NATIVE_CODE.test(localToString.call(localToString)) || own.includes('prototype');
      }, false) ?? false);

    return {
      pristineRealmAvailable: realm !== null,
      checks,
      tamperedCount: tampered.length,
      tamperedPaths: tampered.map((c) => c.path),
      toStringItselfTampered,
      proxyOnNavigator:
        checks.some((c) => c.path.startsWith('Navigator.prototype') && c.foreignReceiverAccepted),
      proxyOnScreen:
        checks.some((c) => c.path.startsWith('Screen.prototype') && c.foreignReceiverAccepted),
    };
  },
});
